/**
 * KYRDROS KS-SEC-1 — WebAuthn/passkey MFA browser helpers.
 * Thin wrapper around @simplewebauthn/browser (handles all base64url <->
 * ArrayBuffer conversion for us; matches the wire format py_webauthn expects
 * on the backend — no custom crypto or manual encoding here).
 */
import { startRegistration, startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import api from '@/lib/api';

export const passkeySupported = () => browserSupportsWebAuthn();

/** Enroll a new passkey for the current (already fully-authenticated) privileged user. */
export async function enrollPasskey(name) {
  const { data: optionsResp } = await api.post('/auth/webauthn/enroll/options');
  const optionsJSON = JSON.parse(optionsResp.options);
  const credential = await startRegistration({ optionsJSON });
  const { data } = await api.post('/auth/webauthn/enroll/verify', { credential, name });
  return data;
}

export async function listPasskeys() {
  const { data } = await api.get('/auth/webauthn/credentials');
  return data;
}

export async function removePasskey(id) {
  const { data } = await api.delete(`/auth/webauthn/credentials/${id}`);
  return data;
}

/** Step-up during login. `csrfToken` comes from the initial /auth/login response body. */
export async function stepUpLogin(csrfToken) {
  const optsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/webauthn/login/challenge`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-CSRF-Token': csrfToken },
  });
  if (!optsRes.ok) {
    const body = await optsRes.json().catch(() => ({}));
    throw new Error(body.detail || 'Could not start passkey verification.');
  }
  const { options } = await optsRes.json();
  const optionsJSON = JSON.parse(options);
  const assertion = await startAuthentication({ optionsJSON });

  const verifyRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/webauthn/login/verify`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-CSRF-Token': csrfToken, 'Content-Type': 'application/json' },
    body: JSON.stringify({ credential: assertion }),
  });
  if (!verifyRes.ok) {
    const body = await verifyRes.json().catch(() => ({}));
    throw new Error(body.detail || 'Passkey verification failed.');
  }
  return verifyRes.json();
}
